import { removeAttributeIfPresent, setAttributeIfChanged } from "../utils/dom"
import { upsertCSSToFrame } from "../utils/frames"
import type { EnhancerOutput, RootEnhancer } from "./types/enhancer"

/**
 *
 */
export const accessibilityEnhancer =
  <InheritOptions, InheritOutput extends EnhancerOutput<RootEnhancer>>(
    next: (options: InheritOptions) => InheritOutput,
  ) =>
  (options: InheritOptions): InheritOutput => {
    const reader = next(options)

    const IntersectionObserverCtor =
      reader.context.document.defaultView?.IntersectionObserver ??
      IntersectionObserver

    const observer = new IntersectionObserverCtor((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          removeAttributeIfPresent(entry.target, `tab-index`)
        } else {
          setAttributeIfChanged(entry.target, `tab-index`, `-1`)
        }
      })
    }, {})

    const cleanupByItemId = new Map<string, () => void>()

    reader.hookManager.register(`item.onDocumentLoad`, async ({ itemId }) => {
      cleanupByItemId.get(itemId)?.()
      cleanupByItemId.delete(itemId)

      const item = reader.spineItemsManager.get(itemId)

      if (!item) return

      const frame = item.renderer.getDocumentFrame()

      if (!frame) return

      upsertCSSToFrame(
        frame,
        `prose-reader-accessibility`,
        `
              :focus-visible {
                ${
                  /*
                  Some epubs remove the outline, this is not good practice since it reduce accessibility.
                  We will try to restore it by force.
                */ ``
                }
                outline: -webkit-focus-ring-color auto 1px;
              }
            `,
      )

      const links = frame.contentDocument?.body.querySelectorAll(`a`)

      links?.forEach((link) => {
        observer.observe(link)
      })

      const cleanup = () => {
        links?.forEach((link) => {
          observer.unobserve(link)
        })
      }

      cleanupByItemId.set(itemId, cleanup)
    })

    reader.hookManager.register(`item.onDocumentUnload`, ({ itemId }) => {
      cleanupByItemId.get(itemId)?.()
      cleanupByItemId.delete(itemId)
    })

    return {
      ...reader,
    }
  }
